import {
    EMPTY_GROUP_SORT_ORDER_SENTINEL,
    getPredefinedGroupLabel,
    orderedPredefinedLeafTypes,
} from "@common/constants/domains/accounting/accountGroups";
import { AccountType } from "@common/constants/domains/accounting/accounts";
import {
    getEntityRank,
    mergeGroupsForDisplay,
    MergedGroup,
} from "@common/helpers/domains/accounting/entityGroupMerge";
import {
    getAccountDisplayKey,
    UNGROUPED_PARENT_IDENTITY,
} from "@common/helpers/domains/accounting/chartRowDisplayKeys";
import { resolveMergedAccountSortOrder } from "@common/helpers/domains/accounting/chartRowShaping";
import { DraftAccount, DraftGroup } from "../draftState";
import { compareLeafSiblings, getTypeHeaderRowId } from "./buildNestedRows";
import { draftGroupsToAccountGroups } from "./draftGroupsToAccountGroups";
import { NestedEditorRow } from "./nestedRowTypes";
import {
    pruneFilteredNestedTypeRows,
    wrapNestedTypeRowsInSections,
} from "./nestedSectionWrapping";

interface BuildMergedNestedRowsParams {
    accounts: DraftAccount[];
    groups: DraftGroup[];
    displayedEntityIds: number[];
    // Draft id -> the Add click that minted it. Two pending Adds in one parent
    // are two rows even though both are still blank.
    pendingGestureIdByDraftId?: ReadonlyMap<string, string>;
    hasActiveFilter?: boolean;
}

interface MergedAccountBucket {
    displayKey: string;
    seed: DraftAccount;
    constituents: DraftAccount[];
}

export const buildMergedNestedRows = ({
    accounts,
    groups,
    displayedEntityIds,
    pendingGestureIdByDraftId,
    hasActiveFilter = false,
}: BuildMergedNestedRowsParams): NestedEditorRow[] => {
    // Filtering the draft BEFORE the merge is what makes `[]` an empty tree:
    // handing `[]` straight to mergeGroupsForDisplay would be read as "no
    // filter" and return every group.
    const entityIdSet = new Set(displayedEntityIds);
    const scopedAccounts = accounts.filter((account) =>
        entityIdSet.has(account.entityId),
    );
    const scopedGroups = groups.filter((group) =>
        entityIdSet.has(group.entityId),
    );
    if (displayedEntityIds.length === 0) {
        return [];
    }

    const draftGroupById = new Map(
        scopedGroups.map((group) => [group.id, group]),
    );
    const { groupsById, mergedGroupsByParentKey, groupKeyById } =
        mergeGroupsForDisplay({
            groups: draftGroupsToAccountGroups(scopedGroups),
            selectedEntityIds: displayedEntityIds,
        });
    const selectedEntityRank = new Map(
        displayedEntityIds.map((entityId, index) => [entityId, index]),
    );

    const parentGroupIdentityOf = (account: DraftAccount): string =>
        account.parentGroupId && groupsById.has(account.parentGroupId)
            ? (groupKeyById.get(account.parentGroupId) ??
              UNGROUPED_PARENT_IDENTITY)
            : UNGROUPED_PARENT_IDENTITY;

    const bucketsByDisplayKey = new Map<string, DraftAccount[]>();
    for (const account of scopedAccounts) {
        const displayKey = getAccountDisplayKey({
            codeText: account.codeText,
            name: account.name,
            isDisabled: account.isDisabled,
            type: account.type,
            parentGroupIdentity: parentGroupIdentityOf(account),
        });
        const gestureId = pendingGestureIdByDraftId?.get(account.id);
        const bucketKey =
            gestureId === undefined ? displayKey : `${displayKey}#${gestureId}`;
        const bucket = bucketsByDisplayKey.get(bucketKey) ?? [];
        bucket.push(account);
        bucketsByDisplayKey.set(bucketKey, bucket);
    }

    const rankAccounts = (constituents: DraftAccount[]): DraftAccount[] =>
        [...constituents].sort(
            (a, b) =>
                getEntityRank(a.entityId, selectedEntityRank) -
                getEntityRank(b.entityId, selectedEntityRank),
        );

    const bucketsByParentIdentity = new Map<string, MergedAccountBucket[]>();
    for (const [displayKey, constituents] of bucketsByDisplayKey) {
        const ranked = rankAccounts(constituents);
        // One account per business per row, so two same-business duplicates
        // stay separately editable.
        const firstByEntity: DraftAccount[] = [];
        const extraSameEntityAccounts: DraftAccount[] = [];
        const seenEntityIds = new Set<number>();
        for (const account of ranked) {
            if (seenEntityIds.has(account.entityId)) {
                extraSameEntityAccounts.push(account);
                continue;
            }
            seenEntityIds.add(account.entityId);
            firstByEntity.push(account);
        }
        const parentIdentity = parentGroupIdentityOf(ranked[0]);
        const siblings = bucketsByParentIdentity.get(parentIdentity) ?? [];
        siblings.push({
            displayKey,
            seed: firstByEntity[0],
            constituents: firstByEntity,
        });
        for (const account of extraSameEntityAccounts) {
            siblings.push({
                displayKey: `${displayKey}#${account.id}`,
                seed: account,
                constituents: [account],
            });
        }
        bucketsByParentIdentity.set(parentIdentity, siblings);
    }

    const constituentDraftGroups = (mergedGroup: MergedGroup): DraftGroup[] =>
        mergedGroup.groups
            .map((group) => draftGroupById.get(group.id))
            .filter((group): group is DraftGroup => group != null);

    const accountRowSortOrder = (row: NestedEditorRow): number =>
        resolveMergedAccountSortOrder(row.accounts.map((a) => a.sortOrder));
    const groupRowSortOrder = (row: NestedEditorRow): number => {
        const stored = row.groups
            .map((g) => g.sortOrder)
            .filter((order) => order !== EMPTY_GROUP_SORT_ORDER_SENTINEL);
        return stored.length > 0
            ? Math.min(...stored)
            : EMPTY_GROUP_SORT_ORDER_SENTINEL;
    };

    const sortSiblings = (rows: NestedEditorRow[]): NestedEditorRow[] =>
        [...rows].sort((a, b) =>
            compareLeafSiblings(
                {
                    id: a.id,
                    sortOrder:
                        a.kind === "account"
                            ? accountRowSortOrder(a)
                            : groupRowSortOrder(a),
                    kind: a.kind,
                    name: a.name,
                },
                {
                    id: b.id,
                    sortOrder:
                        b.kind === "account"
                            ? accountRowSortOrder(b)
                            : groupRowSortOrder(b),
                    kind: b.kind,
                    name: b.name,
                },
            ),
        );

    const buildAccountRow = (
        bucket: MergedAccountBucket,
        depth: number,
    ): NestedEditorRow => ({
        id: bucket.displayKey,
        kind: "account",
        name: bucket.seed.name,
        depth,
        account: bucket.seed,
        accountType: bucket.seed.type,
        accounts: bucket.constituents,
        groups: [],
    });

    const buildGroupRow = (
        mergedGroup: MergedGroup,
        depth: number,
    ): NestedEditorRow => {
        const constituents = constituentDraftGroups(mergedGroup);
        const seed = constituents[0];
        const childGroups = mergedGroupsByParentKey.get(mergedGroup.key) ?? [];
        const childGroupRows = childGroups.map((child) =>
            buildGroupRow(child, depth + 1),
        );
        const childAccountRows = (
            bucketsByParentIdentity.get(mergedGroup.key) ?? []
        ).map((bucket) => buildAccountRow(bucket, depth + 1));
        return {
            id: mergedGroup.key,
            kind: "group",
            name: seed.name,
            depth,
            group: seed,
            accountType: seed.type,
            accounts: [],
            groups: constituents,
            children: sortSiblings([...childGroupRows, ...childAccountRows]),
        };
    };

    const presentTypes = new Set<AccountType>();
    for (const account of scopedAccounts) {
        presentTypes.add(account.type);
    }
    for (const group of scopedGroups) {
        presentTypes.add(group.type);
    }

    const topLevelMergedGroups = mergedGroupsByParentKey.get(null) ?? [];
    const ungroupedBuckets =
        bucketsByParentIdentity.get(UNGROUPED_PARENT_IDENTITY) ?? [];

    const typeRows: NestedEditorRow[] = [];
    for (const type of orderedPredefinedLeafTypes) {
        if (!presentTypes.has(type)) {
            continue;
        }
        const groupRows = topLevelMergedGroups
            .filter((mergedGroup) => mergedGroup.group.type === type)
            .map((mergedGroup) => buildGroupRow(mergedGroup, 1));
        const accountRows = ungroupedBuckets
            .filter((bucket) => bucket.seed.type === type)
            .map((bucket) => buildAccountRow(bucket, 1));
        typeRows.push({
            id: getTypeHeaderRowId(type),
            kind: "type",
            name: getPredefinedGroupLabel(type),
            depth: 0,
            accountType: type,
            accounts: [],
            groups: [],
            children: sortSiblings([...groupRows, ...accountRows]),
        });
    }
    const prunedTypeRows = hasActiveFilter
        ? pruneFilteredNestedTypeRows(typeRows)
        : typeRows;
    return wrapNestedTypeRowsInSections(prunedTypeRows);
};
